import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import { snakeToCamel } from '@rossum/api-client/utils';
import { Chip, Stack, Tab, Tabs, Tooltip } from '@rossum/ui/material';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import { LocalizationKeys } from '../../../i18n';
import { getUpdatedExistingFilter } from '../../document-list-filtering/getUpdatedExistingFilter';
import {
  FilterColDef,
  ValidatedGridFilterItem,
} from '../../document-list-filtering/types';
import { createNewFilterQuery } from '../../mql/transformers';
import { DashboardQuery } from '../hooks/useDashboardQuery';
import { HiddenTabs } from './HiddenTabs';
import { useStatusTabCounts } from './hooks/useStatusTabCounts';
import { useStatusTabs } from './hooks/useStatusTabs';
import { useTabVisibility } from './hooks/useTabVisibility';
import { SavedViewsTab } from './SavedViewsTab';
import { STATUS_TABS_HEIGHT, StatusTabKeys, statusTabsColumnMap } from './tabs';
import {
  getMatchingStatusFilter,
  getTabAnimation,
  isStatusTabColumn,
} from './utils';

const validCountTooltipIds: Partial<Record<StatusTabKeys, LocalizationKeys>> = {
  reviews: 'containers.allDocuments.statusTabs.tooltip.valid.reviews',
  postponed: 'containers.allDocuments.statusTabs.tooltip.valid.postponed',
  confirmed: 'containers.allDocuments.statusTabs.tooltip.valid.confirmed',
  rejected: 'containers.allDocuments.statusTabs.tooltip.valid.rejected',
  in_workflow: 'containers.allDocuments.statusTabs.tooltip.valid.inWorkflow',
};

const invalidCountTooltipIds: Partial<Record<StatusTabKeys, LocalizationKeys>> =
  {
    reviews: 'containers.allDocuments.statusTabs.tooltip.invalid.reviews',
    exports: 'containers.allDocuments.statusTabs.tooltip.invalid.exports',
  };

const getValidCountTooltipId = (key: StatusTabKeys) =>
  validCountTooltipIds[key];

const getInvalidCountTooltipId = (key: StatusTabKeys) =>
  invalidCountTooltipIds[key];

type StatusTabsProps = {
  existingFilterItems: ValidatedGridFilterItem[];
  handleQueryChange: (newQuery: DashboardQuery) => void;
  activeQueue: Queue;
  savedViewId: string | undefined;
  columns: FilterColDef[];
};

export const StatusTabs = ({
  existingFilterItems,
  handleQueryChange,
  activeQueue,
  savedViewId,
  columns,
}: StatusTabsProps) => {
  const intl = useIntl();

  const existingFilterWithoutQueue = existingFilterItems.filter(
    item => item.field !== 'queue'
  );

  const statusTabs = useStatusTabs(activeQueue);

  const { data: statusCounts } = useStatusTabCounts({
    queueId: activeQueue.id,
    statusTabs,
  });

  const matchingStatusFilter = getMatchingStatusFilter({
    existingFilterItems,
    statusTabs,
  });

  const activeTabKey = matchingStatusFilter?.key ?? null;

  const isStatusTabKey = (key: string): key is StatusTabKeys =>
    statusTabs.some(tab => tab.key === key);

  const excludeNonTabColumns = (
    filterItems: GridFilterItem[],
    targetTabKey: StatusTabKeys
  ) => {
    return filterItems.flatMap(item => {
      const tabKey = item.field;
      if (!isStatusTabColumn(tabKey)) return [item];

      const matchingStatusTabs = statusTabsColumnMap[tabKey];

      if (matchingStatusTabs) {
        return matchingStatusTabs.some(statusTab => statusTab === targetTabKey)
          ? [item]
          : [];
      }
      return [item];
    });
  };

  const handleFilterModelChange = (newFilterItems: GridFilterItem[]) =>
    handleQueryChange({ filtering: createNewFilterQuery(newFilterItems) });

  const handleChange = (newValue: unknown) => {
    if (typeof newValue !== 'string' || !isStatusTabKey(newValue)) return;

    const targetTab = statusTabs.find(tab => tab.key === newValue);

    if (!targetTab) return;

    const newFilter = getUpdatedExistingFilter({
      existingFilterItems,
      incomingFilters: [targetTab.filter],
      isReplaceAction: existingFilterItems.some(
        item => item.field === 'status'
      ),
    });

    const updatedFilter = excludeNonTabColumns(newFilter, newValue);

    const sortedFilter = updatedFilter
      .map(f => f)
      .sort(a => (a.field === 'status' ? -1 : 1));

    handleFilterModelChange(sortedFilter);
  };

  const {
    setContainerSizeRef,
    setTabSizes,
    visibleTabs,
    hiddenTabs,
    setMoreButtonSize,
    readyToDisplay,
  } = useTabVisibility({
    activeTabKey,
    allStatusTabs: statusTabs,
  });

  return (
    <Stack direction="row" sx={{ flexGrow: 1 }}>
      <Stack
        direction="row"
        ref={setContainerSizeRef}
        sx={{
          visibility: readyToDisplay ? 'visible' : 'hidden',
          flexGrow: 1,
        }}
      >
        <Tabs
          // MUI expects `false` when no tab is selected
          value={activeTabKey ?? false}
          onChange={(_, newValue) => handleChange(newValue)}
          sx={{
            height: STATUS_TABS_HEIGHT,
            minHeight: STATUS_TABS_HEIGHT,
            overflow: 'hidden',
          }}
        >
          {visibleTabs.map(({ key }, index) => {
            const { validCount, invalidCount } = statusCounts?.[key] ?? {};
            const isTabActive = key === activeTabKey;
            return (
              <Tab
                ref={el => setTabSizes(el, key)}
                label={intl.formatMessage({
                  id: `containers.allDocuments.statusTabs.${snakeToCamel(key)}`,
                })}
                data-id={snakeToCamel(key)}
                key={key}
                value={key}
                sx={{
                  '&.Mui-selected': { color: 'text.primary' },
                  flexShrink: 0,
                  height: STATUS_TABS_HEIGHT,
                  minHeight: STATUS_TABS_HEIGHT,
                  ...getTabAnimation({
                    startAnimation: readyToDisplay,
                    delayIndex: index,
                  }),
                }}
                iconPosition="end"
                icon={
                  statusCounts ? (
                    <Stack direction="row" spacing={1}>
                      {validCount && getValidCountTooltipId(key) ? (
                        <Tooltip
                          title={intl.formatMessage({
                            id: getValidCountTooltipId(key),
                          })}
                        >
                          <Chip
                            label={validCount}
                            color={isTabActive ? 'info' : 'default'}
                          />
                        </Tooltip>
                      ) : null}
                      {invalidCount && getInvalidCountTooltipId(key) ? (
                        <Tooltip
                          title={intl.formatMessage({
                            id: getInvalidCountTooltipId(key),
                          })}
                        >
                          <Chip label={invalidCount} color="error" />
                        </Tooltip>
                      ) : null}
                    </Stack>
                  ) : undefined
                }
              />
            );
          })}
        </Tabs>
        {hiddenTabs.length ? (
          <HiddenTabs
            setMoreButtonSize={setMoreButtonSize}
            hiddenTabs={hiddenTabs}
            onClick={tab => handleChange(tab.key)}
            sx={getTabAnimation({
              startAnimation: readyToDisplay,
              delayIndex: visibleTabs.length,
            })}
          />
        ) : null}
      </Stack>
      <Stack sx={{ alignSelf: 'flex-end' }}>
        <SavedViewsTab
          existingFilterWithoutQueue={existingFilterWithoutQueue}
          handleQueryChange={handleQueryChange}
          columns={columns}
          activeQueueId={activeQueue.id}
          savedViewId={savedViewId}
          isActive={!activeTabKey}
        />
      </Stack>
    </Stack>
  );
};
